import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '../../../../../../api/client';

interface TranscriptionSegment {
  index: number;
  start: string;
  end: string;
  text: string;
  originalText?: string;
}

interface UseSegmentPlaybackOptions {
  masterToken: string;
  previewUrl: string;
  processedVideoUrl: string;
}

export function useSegmentPlayback({ masterToken, previewUrl, processedVideoUrl }: UseSegmentPlaybackOptions) {
  const [playingSegment, setPlayingSegment] = useState<{ index: number; type: 'original' | 'processed' } | null>(null);
  const activePlaybackRef = useRef<HTMLVideoElement | null>(null);

  const [cachedVoiceSamples, setCachedVoiceSamples] = useState<Record<string, string>>({});
  const [playingSampleVoice, setPlayingSampleVoice] = useState<string | null>(null);

  const parseTimeToSeconds = (timeStr: string): number => {
    if (!timeStr) return 0;
    const parts = timeStr.trim().split(':');
    let hrs = 0, mins = 0, secs = 0;
    if (parts.length === 3) {
      hrs = parseInt(parts[0], 10);
      mins = parseInt(parts[1], 10);
      secs = parseFloat(parts[2]);
    } else if (parts.length === 2) {
      mins = parseInt(parts[0], 10);
      secs = parseFloat(parts[1]);
    } else {
      secs = parseFloat(parts[0]);
    }
    return (hrs * 3600) + (mins * 60) + secs;
  };

  const playSegment = (videoUrl: string, startStr: string, endStr: string, index: number, type: 'original' | 'processed') => {
    if (activePlaybackRef.current) {
      activePlaybackRef.current.pause();
      activePlaybackRef.current.remove();
      activePlaybackRef.current = null;
    }

    const startSec = parseTimeToSeconds(startStr);
    const endSec = parseTimeToSeconds(endStr);

    const videoEl = document.createElement('video');
    videoEl.crossOrigin = 'anonymous';
    videoEl.src = `${videoUrl}#t=${startSec},${endSec}`;
    videoEl.style.display = 'none';
    document.body.appendChild(videoEl);
    activePlaybackRef.current = videoEl;

    setPlayingSegment({ index, type });

    videoEl.play().catch(err => {
      console.warn("Falha ao iniciar reprodução do trecho:", err);
      setPlayingSegment(null);
    });

    const checkTime = () => {
      if (videoEl.currentTime >= endSec) {
        videoEl.pause();
        videoEl.removeEventListener('timeupdate', checkTime);
        if (activePlaybackRef.current === videoEl) {
          setPlayingSegment(null);
          activePlaybackRef.current = null;
        }
        videoEl.remove();
      }
    };
    videoEl.addEventListener('timeupdate', checkTime);

    videoEl.onended = () => {
      if (activePlaybackRef.current === videoEl) {
        setPlayingSegment(null);
        activePlaybackRef.current = null;
      }
      videoEl.remove();
    };
  };

  /**
   * playSegmentBruto — plays the ORIGINAL audio.webm for a segment time range.
   * Used by the "Bruto" button since video.webm is recorded mute (video track only).
   */
  const playSegmentBruto = (startStr: string, endStr: string, index: number) => {
    if (activePlaybackRef.current) {
      activePlaybackRef.current.pause();
      activePlaybackRef.current.remove();
      activePlaybackRef.current = null;
    }

    // Resolve original audio URL from window global or sessionStorage
    let audioUrl = (window as any).__ecadLastAudioUrl || '';
    if (!audioUrl) {
      try {
        const saved = sessionStorage.getItem('ecad_session');
        if (saved) audioUrl = JSON.parse(saved).audioUrl || '';
      } catch {}
    }
    if (!audioUrl) {
      console.warn('[playSegmentBruto] Nenhum áudio original disponível');
      return;
    }

    // Convert to serve-local URL if needed
    const serveUrl = audioUrl.startsWith('http://') || audioUrl.startsWith('https://')
      ? audioUrl
      : audioUrl.startsWith('/api/')
        ? audioUrl
        : audioUrl.replace(/^\/ecad\//, '/api/v1/ecad/serve-local/');

    const startSec = parseTimeToSeconds(startStr);
    const endSec = parseTimeToSeconds(endStr);

    const audioEl = document.createElement('audio') as HTMLAudioElement;
    audioEl.src = serveUrl;
    audioEl.style.display = 'none';
    document.body.appendChild(audioEl);
    activePlaybackRef.current = audioEl as any;

    setPlayingSegment({ index, type: 'original' });

    // Seek to start, then play
    audioEl.currentTime = startSec;
    audioEl.play().catch(err => {
      console.warn('[playSegmentBruto] Falha ao tocar áudio original:', err);
      setPlayingSegment(null);
    });

    const checkTime = () => {
      if (audioEl.currentTime >= endSec) {
        audioEl.pause();
        audioEl.removeEventListener('timeupdate', checkTime);
        if (activePlaybackRef.current === (audioEl as any)) {
          setPlayingSegment(null);
          activePlaybackRef.current = null;
        }
        audioEl.remove();
      }
    };
    audioEl.addEventListener('timeupdate', checkTime);

    audioEl.onended = () => {
      if (activePlaybackRef.current === (audioEl as any)) {
        setPlayingSegment(null);
        activePlaybackRef.current = null;
      }
      audioEl.remove();
    };
  };

  const stopSegment = () => {
    if (activePlaybackRef.current) {
      activePlaybackRef.current.pause();
      activePlaybackRef.current.remove();
      activePlaybackRef.current = null;
    }
    setPlayingSegment(null);
  };

  const playVoiceSample = async (voiceName: string) => {
    if (playingSampleVoice === voiceName) {
      if (activePlaybackRef.current) {
        activePlaybackRef.current.pause();
        activePlaybackRef.current.remove();
        activePlaybackRef.current = null;
      }
      setPlayingSampleVoice(null);
      return;
    }

    stopSegment();

    let audioUrl = cachedVoiceSamples[voiceName];
    if (!audioUrl) {
      setPlayingSampleVoice(voiceName);
      toast.loading(`Sintetizando amostra de voz para ${voiceName}...`, { id: 'voice-sample' });
      try {
        const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
        const frase = `Olá! Sou a voz brasileira ${voiceName}. Estou pronta para guiar esta aula no Aluno Conectado.`;
        const res = await api.post('/ecad/sintetizar', {
          text: frase,
          voice: voiceName,
          force: false
        }, { headers });

        if (res.data && res.data.ok && res.data.audioUrl) {
          audioUrl = res.data.audioUrl;
          setCachedVoiceSamples(prev => ({ ...prev, [voiceName]: audioUrl }));
          toast.success(`Amostra de ${voiceName} carregada!`, { id: 'voice-sample' });
        } else {
          toast.error('Erro ao gerar amostra de voz.', { id: 'voice-sample' });
          setPlayingSampleVoice(null);
          return;
        }
      } catch (err: any) {
        console.error('Erro ao gerar amostra:', err);
        toast.error('Falha ao gerar amostra de voz.', { id: 'voice-sample' });
        setPlayingSampleVoice(null);
        return;
      }
    }

    if (audioUrl) {
      const audioEl = new Audio(audioUrl);
      activePlaybackRef.current = audioEl as any;
      setPlayingSampleVoice(voiceName);
      audioEl.play().catch(() => {
        setPlayingSampleVoice(null);
      });
      audioEl.onended = () => {
        setPlayingSampleVoice(null);
      };
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activePlaybackRef.current) {
        activePlaybackRef.current.pause();
        activePlaybackRef.current.remove();
      }
    };
  }, []);

  return {
    playingSegment,
    setPlayingSegment,
    playSegment,
    playSegmentBruto,
    stopSegment,
    playVoiceSample,
    playingSampleVoice,
    parseTimeToSeconds
  };
}
